/* eslint-disable react/no-multi-comp */
import { useState, useEffect } from 'react';
import cn from 'classnames';
import { List } from 'immutable';
import { useNavigate, NavigateFunction } from 'react-router';
import { Link } from 'react-router-dom';
import { DomainContext, DOMAIN_CONTEXT } from '@mapado/js-component';
import { MpdLoader, MpdIcon, MpdCheckbox } from '@mapado/makeup';
import TicketingSdk, { User, Fields } from '@mapado/ticketing-js-sdk';
import DemoContent from './DemoContent';
import { SeatConfigShape } from './DemoTypes';
import { RecordLatestPage } from './LatestPage';
import LinkMenu from './LinkMenu';
import { useTranslation } from './i18n';
import redirectToLoginPage from './redirectToLoginPage';
import useQueryNumberParam from './useQueryNumberParam';

const USER_FIELDS: Fields = [
  'avatar',
  'email',
  'fullName',
  'impersonated',
  'contractUserList',
  'realFullName',
] as const;

type DemoProps = {
  onGoToLogin: (navigate: NavigateFunction, errorMessage?: string) => void;
  sdk: TicketingSdk;
};

export function DemoLayout({ onGoToLogin, sdk }: DemoProps) {
  const navigate = useNavigate();
  const [seatConfigList, setsSeatConfigList] =
    useState<List<SeatConfigShape>>(List());
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [mountKey, setMountKey] = useState(Date.now());
  const [domainContext, setDomainContext] = useState(DOMAIN_CONTEXT.DESK);
  const isLivePdfRendering = useQueryNumberParam('pdf', 0);

  const simulateUnmountAndRemount = (): void => {
    setMountKey(Date.now());
  };
  const switchContext = (): void => {
    setDomainContext((prev) =>
      prev === DOMAIN_CONTEXT.DESK
        ? DOMAIN_CONTEXT.MINISITE
        : DOMAIN_CONTEXT.DESK
    );
  };

  useEffect(() => {
    Promise.all([
      sdk.getRepository('user').getCurrent(USER_FIELDS),
      sdk.getRepository('seatConfig').findBy(
        {
          itemsPerPage: 99999,
        },
        [
          '@id',
          'label',
          'totalStock',
          {
            contract: ['@id', 'companyDisplayName'],
          },
        ] as const
      ),
    ])
      .then(([user, seatConfigCollection]) => {
        // @ts-expect-error -- custom serializer typing error
        let seatConfigArray = seatConfigCollection.get(
          'hydra:member'
        ) as Array<SeatConfigShape>;

        seatConfigArray = seatConfigArray.filter(
          (seatConfig: SeatConfigShape): boolean => seatConfig.contract !== null
        );
        setCurrentUser(user);
        setsSeatConfigList(List(seatConfigArray));
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
        onGoToLogin(
          navigate,
          e?.baseResponse?.status === 403
            ? 'Forbidden error : Are you impersonnated ?'
            : e?.message
        );
      });
  }, [navigate, onGoToLogin, sdk]);

  if (!currentUser) {
    return (
      <div className="seating-demo__loaderCentered">
        <MpdLoader />
      </div>
    );
  }

  // TODO: migrate that to nuqs once we are on react >= 18.2
  const setIsLivePdfRendering = (value: number): void => {
    const searchParams = new URLSearchParams(window.location.search);

    if (value === 1) {
      searchParams.set('pdf', '1');
    } else {
      searchParams.delete('pdf');
    }

    navigate({
      search: searchParams.toString(),
    });
  };

  return (
    <div className="seating-demo-root-wrapper">
      <div
        className={cn(
          'seating-demo-header',
          currentUser.impersonated && 'seating-demo-header--impersonnated'
        )}
      >
        <span>
          {currentUser.realFullName}
          {currentUser.impersonated && ` (impersonné en ${currentUser.email})`}
        </span>
        <div>
          <Link to="/logout">Déconnexion</Link>
        </div>
      </div>

      <LinkMenu />

      <div className="seating-demo-root">
        <div className="seating-demo-root__content">
          <div>
            <button
              type="button"
              className="mpd-btn mpd-btn--link mpd-btn--no-padding mb1"
              onClick={simulateUnmountAndRemount}
            >
              <MpdIcon icon="magnifying-glass" height="16" width="16" />
              <span>Simulate unmount + remount</span>
            </button>
            <button
              type="button"
              className="mpd-btn mpd-btn--link mpd-btn--no-padding mb1"
              onClick={switchContext}
            >
              <MpdIcon icon="refresh" height="16" width="16" />
              <span>Switch domain context (actual: {domainContext})</span>
            </button>

            <MpdCheckbox
              className="ml2 mb1 inbl"
              checked={isLivePdfRendering === 1}
              onChange={() =>
                setIsLivePdfRendering(isLivePdfRendering === 1 ? 0 : 1)
              }
            >
              Live PDF rendering
            </MpdCheckbox>
          </div>

          <DomainContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
              currentDomain: domainContext,
              deskDomain: '',
            }}
          >
            <RecordLatestPage />
            <DemoContent
              key={`${mountKey}-${isLivePdfRendering ? '-with-pdf' : ''}`}
              seatConfigList={seatConfigList}
              isLivePdfRendering={isLivePdfRendering === 1}
            />
          </DomainContext.Provider>
        </div>
      </div>
    </div>
  );
}

export function Login(): JSX.Element {
  const { t } = useTranslation('admin');

  const errorMessage = new URLSearchParams(window.location.search).get(
    'errorMessage'
  );

  return (
    <div className="seating-demo__loaderCentered">
      <div className="mpd-block-content">
        <button
          type="button"
          className="mpd-btn mpd-btn--primary"
          onClick={() => {
            redirectToLoginPage(
              new URLSearchParams(window.location.search).get('redirectUrl') ||
                '/'
            );
          }}
        >
          {t('login.connect_with_mapado')}
        </button>
      </div>

      {errorMessage && (
        <div className="mpd-alert mpd-alert--error">{errorMessage}</div>
      )}
    </div>
  );
}

export function Logout({ ticketingSdk }: { ticketingSdk: TicketingSdk }) {
  const navigate = useNavigate();

  useEffect(() => {
    ticketingSdk.tokenStorage.logout().then(() => {
      navigate('/');
    });
  }, [navigate, ticketingSdk.tokenStorage]);

  return null;
}
